import React, { useState, useRef, useMemo } from "react";
import { LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { DateGrid } from "Components/atoms/DateGrid";
import { DatePickerActions } from "Components/atoms/DatePickerActions/Desktop";
import { DatePickerTop } from "Components/atoms/DatePickerTop";
import { generateDaysInMonth } from "shared/utils/generateDaysInMonth";
import { getFormattedDateDetails } from "shared/utils/getFormattedDateDetails";
import { useClickOutside } from "shared/hooks/useClickOutside";
import { formatDateRangeShort } from "shared/utils/alerts/alertFormat";
import { AlertDialog } from "../AlertDialog";
import {
  DateRangePickerWrapper,
  TriggerButton,
  TriggerPlaceholder,
  Popover,
  CalendarsContainer,
  CalendarMonthContainer,
  MonthHeader,
  MonthTitle,
  MonthNavButton,
  PopoverFooter,
  SelectionSummary,
  FooterActions,
  ClearButton,
  ConfirmButton,
} from "./styles";
import { AlertDateRangePickerProps } from "./types";

/**
 * Componente AlertDateRangePicker
 *
 * Seletor de intervalo de datas com interface diferenciada para desktop
 * (popover com 2 meses) e mobile (diálogo com 1 mês).
 *
 * Regra de seleção:
 * - Draft vazio ou com 2 datas: ao clicar, define `[date]`
 * - Draft com 1 data e `date > draft[0]`: define `[draft[0], date]`
 * - Outros casos: define `[date]`
 */
export const AlertDateRangePicker: React.FC<AlertDateRangePickerProps> = ({
  label,
  placeholder,
  value,
  onChange,
  minDate,
  disabled = false,
  isMobile,
  variant,
  popoverAlign,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(value);
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    return new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate(),
      0,
      0,
      0,
      0,
    );
  });

  const wrapperRef = useRef<HTMLDivElement>(null);

  // Desktop: fecha ao clicar fora
  useClickOutside(isMobile ? [] : [wrapperRef], () => setIsOpen(false));

  // Sincroniza draft com value quando o picker abre
  const handleOpenPicker = () => {
    setDraft(value);
    const now = new Date();
    const [year, month] = (value[0] ?? "").split("-").map(Number);
    const start = year && month ? new Date(year, month - 1, 1) : null;
    const currentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    setCurrentDate(start && start > currentMonth ? start : currentMonth);
    setIsOpen(true);
  };

  // Regra de seleção conforme o plano
  const handleSelectDate = (date: string) => {
    if (draft.length === 0 || draft.length === 2) {
      setDraft([date]);
    } else if (draft.length === 1 && date > draft[0]) {
      setDraft([draft[0], date]);
    } else {
      setDraft([date]);
    }
  };

  const handleClear = () => {
    setDraft([]);
  };

  const handleConfirm = () => {
    onChange(draft);
    setIsOpen(false);
  };

  // Calcula os dias cinza (anteriores a minDate)
  const grayDates = useMemo(() => {
    if (!minDate) return undefined;
    const minDateObj = new Date(minDate);
    const gray: string[] = [];
    const year = minDateObj.getFullYear();
    const month = minDateObj.getMonth();
    const day = minDateObj.getDate();

    // Todos os dias antes de minDate no mesmo mês
    for (let d = 1; d < day; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      gray.push(dateStr);
    }

    return gray;
  }, [minDate]);

  const changeMonth = (offset: number) => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1),
    );
  };

  const today = new Date();
  const disablePrevious =
    currentDate.getMonth() === today.getMonth() &&
    currentDate.getFullYear() === today.getFullYear();

  // Mobile: renderiza como diálogo
  if (isMobile) {
    const monthDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1,
    );
    const formatted = getFormattedDateDetails(monthDate);
    const days = generateDaysInMonth(
      monthDate.getFullYear(),
      monthDate.getMonth(),
      [],
      [],
    );

    return (
      <DateRangePickerWrapper ref={wrapperRef}>
        <TriggerButton
          onClick={handleOpenPicker}
          disabled={disabled}
          type="button"
          $variant={variant}
        >
          {value.length === 0 ? (
            <TriggerPlaceholder $variant={variant}>
              {placeholder}
            </TriggerPlaceholder>
          ) : (
            formatDateRangeShort(value)
          )}
        </TriggerButton>

        <AlertDialog
          isOpen={isOpen}
          title={label}
          onClose={() => setIsOpen(false)}
          footer={
            <DatePickerActions
              onClear={handleClear}
              onConfirm={handleConfirm}
            />
          }
        >
          <DatePickerTop
            month={formatted.monthFull || ""}
            year={formatted.year || ""}
            onPreviousMonth={
              !disablePrevious ? () => changeMonth(-1) : undefined
            }
            onNextMonth={() => changeMonth(1)}
          />
          <DateGrid
            days={days}
            currentDate={monthDate}
            selectedDates={draft}
            onDatesChange={() => {}}
            onSelectDate={(day) => {
              const date = new Date(
                monthDate.getFullYear(),
                monthDate.getMonth(),
                day,
              );
              const formattedDate = `${date.getFullYear()}-${String(
                date.getMonth() + 1,
              ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              handleSelectDate(formattedDate);
            }}
            rangeStart={draft[0]}
            rangeEnd={draft[1]}
            grayDates={grayDates}
            continuousRange
          />
        </AlertDialog>
      </DateRangePickerWrapper>
    );
  }
  const summary =
    draft.length === 0
      ? "Escolha o primeiro dia"
      : draft.length === 1
        ? `${formatDateRangeShort(draft)} · agora escolha o último dia`
        : formatDateRangeShort(draft);

  // Desktop: renderiza como popover com 2 meses
  return (
    <DateRangePickerWrapper ref={wrapperRef}>
      <TriggerButton
        onClick={handleOpenPicker}
        disabled={disabled}
        type="button"
        $variant={variant}
      >
        {value.length === 0 ? (
          <TriggerPlaceholder $variant={variant}>
            {placeholder}
          </TriggerPlaceholder>
        ) : (
          formatDateRangeShort(value)
        )}
      </TriggerButton>

      {isOpen && (
        <Popover $variant={variant} $align={popoverAlign}>
          <CalendarsContainer>
            {[0, 1].map((offset) => {
              const monthDate = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth() + offset,
                1,
              );
              const formatted = getFormattedDateDetails(monthDate);
              const days = generateDaysInMonth(
                monthDate.getFullYear(),
                monthDate.getMonth(),
                [],
                [],
              );

              return (
                <CalendarMonthContainer
                  key={`${monthDate.getFullYear()}-${monthDate.getMonth()}`}
                >
                  <MonthHeader>
                    {offset === 0 ? (
                      <MonthNavButton
                        type="button"
                        onClick={() => changeMonth(-1)}
                        disabled={disablePrevious}
                        aria-label="Mês anterior"
                      >
                        <LuChevronLeft />
                      </MonthNavButton>
                    ) : (
                      <span />
                    )}
                    <MonthTitle>
                      {formatted.monthFull} {formatted.year}
                    </MonthTitle>
                    {offset === 1 ? (
                      <MonthNavButton
                        type="button"
                        onClick={() => changeMonth(1)}
                        aria-label="Próximo mês"
                      >
                        <LuChevronRight />
                      </MonthNavButton>
                    ) : (
                      <span />
                    )}
                  </MonthHeader>
                  <DateGrid
                    days={days}
                    currentDate={monthDate}
                    selectedDates={draft}
                    onDatesChange={() => {}}
                    onSelectDate={(day) => {
                      const date = new Date(
                        monthDate.getFullYear(),
                        monthDate.getMonth(),
                        day,
                      );
                      const formattedDate = `${date.getFullYear()}-${String(
                        date.getMonth() + 1,
                      ).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
                      handleSelectDate(formattedDate);
                    }}
                    rangeStart={draft[0]}
                    rangeEnd={draft[1]}
                    grayDates={grayDates}
                    continuousRange
                  />
                </CalendarMonthContainer>
              );
            })}
          </CalendarsContainer>
          <PopoverFooter>
            <SelectionSummary aria-live="polite">{summary}</SelectionSummary>
            <FooterActions>
              <ClearButton type="button" onClick={handleClear}>
                Limpar
              </ClearButton>
              <ConfirmButton type="button" onClick={handleConfirm}>
                Confirmar
              </ConfirmButton>
            </FooterActions>
          </PopoverFooter>
        </Popover>
      )}
    </DateRangePickerWrapper>
  );
};
